import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { taskPaymentsApi, otherExpensesApi } from "../lib/api";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { PaymentSourceSelector } from "../components/PaymentSourceSelector";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  DollarSign,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ExternalLink,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Edit,
  ListTodo,
  FileSpreadsheet,
  Calendar,
  ThumbsUp,
  XCircle,
  CreditCard,
  Building2,
  Globe,
  Bitcoin,
  Wallet,
  Eye,
  Plus,
  Loader2,
  User,
  Copy,
  Banknote,
} from "lucide-react";
import { exportToCsv, timestampedName } from "../lib/exportToCsv";
import ExportCurrencyButton, { buildExportPreamble } from "../components/ExportCurrencyButton";

const PaymentsPage = () => {
  const { user } = useAuth();
  const { formatAmount, convertAmount, convertAmountExplicit, displayCurrency, getCurrencySymbol, rates, rateDate } = useCurrency();
  const [taskPayments, setTaskPayments] = useState([]);
  const [artistExpenses, setArtistExpenses] = useState([]);
  const [artistStats, setArtistStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterArtist, setFilterArtist] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");
  // marker: payments-due-date-filter-v1
  const [filterDueFrom, setFilterDueFrom] = useState(""); // YYYY-MM-DD
  const [filterDueTo, setFilterDueTo] = useState("");

  // Edit dialog
  const [editDialog, setEditDialog] = useState({ open: false, expense: null });
  const [editAmount, setEditAmount] = useState("");
  
  // Payment details modal
  const [paymentDetailsModal, setPaymentDetailsModal] = useState({ open: false, payment: null, paymentMethods: null, loading: false });

  // Sorting
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [showBonuses, setShowBonuses] = useState(true);

  // Paid details dialog
  const [paidDialog, setPaidDialog] = useState({ open: false, paymentId: null, type: null });
  const [paidDate, setPaidDate] = useState("");
  const [paidSource, setPaidSource] = useState("");
  const [paidComment, setPaidComment] = useState("");
  const [employees, setEmployees] = useState([]);
  
  // Navigation
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    try {
      // If month is empty, fetch all pending payments
      const monthParam = selectedMonth || undefined;
      
      const [taskRes, expensesRes, statsRes] = await Promise.all([
        // Task payments - filter by month
        monthParam 
          ? taskPaymentsApi.getAll({ month: monthParam })
          : taskPaymentsApi.getAll(),  // Get all when viewing pending
        // Artist expenses - filter by month or get all pending
        monthParam 
          ? otherExpensesApi.getAll({ month: monthParam, expense_type: "artist_payment" })
          : otherExpensesApi.getAll({ expense_type: "artist_payment", status: "approved" }),
        otherExpensesApi.getStats("artist_payment"),
      ]);
      
      // Sort task payments by date
      const sortedTasks = (taskRes.data || []).sort((a, b) => {
        if (a.is_overdue && !b.is_overdue) return -1;
        if (!a.is_overdue && b.is_overdue) return 1;
        const dateA = a.client_confirmed_date ? new Date(a.client_confirmed_date) : new Date(0);
        const dateB = b.client_confirmed_date ? new Date(b.client_confirmed_date) : new Date(0);
        return dateB - dateA;
      });
      
      setTaskPayments(sortedTasks);
      setArtistExpenses(expensesRes.data || []);
      setArtistStats(statsRes.data);
      
      // Load employees for paid_by dropdown
      try {
        const empRes = await api.get("/employees?is_active=true");
        setEmployees(empRes.data || []);
      } catch { /* ignore */ }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  // Open payment details modal and fetch artist/employee payment methods
  const openPaymentDetailsModal = async (payment, type = "task") => {
    setPaymentDetailsModal({ open: true, payment: { ...payment, type }, paymentMethods: null, loading: true });
    
    try {
      let employeeId = payment.artist_id;
      const employeeName = payment.artist_name;
      
      // If no artist_id, try to find employee by name
      if (!employeeId && employeeName) {
        try {
          const staffRes = await api.get(`/employees?search=${encodeURIComponent(employeeName)}`);
          if (staffRes.data && staffRes.data.length > 0) {
            // Find exact or closest match
            const match = staffRes.data.find(s => s.name === employeeName) || staffRes.data[0];
            employeeId = match.id;
          }
        } catch (e) {
          console.log("Could not find employee by name:", e);
        }
      }
      
      if (employeeId) {
        const empRes = await api.get(`/employees/${employeeId}`);
        if (empRes.data) {
          setPaymentDetailsModal(prev => ({ 
            ...prev, 
            paymentMethods: empRes.data.payment_methods || null,
            employeeName: empRes.data.name,
            loading: false 
          }));
          return;
        }
      }
      setPaymentDetailsModal(prev => ({ ...prev, loading: false }));
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setPaymentDetailsModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Helper to parse payment details and determine card type/bank
  const parsePaymentDetails = (details) => {
    if (!details) return null;
    
    const trimmed = details.trim();
    
    // SWIFT/IBAN detection (starts with letters, contains alphanumeric, typically 15-34 chars)
    // Check for IBAN (starts with 2 letters + 2 digits, e.g., UA12...)
    if (/^[A-Z]{2}\d{2}/i.test(trimmed) && trimmed.length >= 15 && trimmed.length <= 34) {
      return { type: 'iban', value: trimmed, label: 'IBAN' };
    }
    
    // Check for SWIFT code (8 or 11 characters, letters and digits)
    const swiftPattern = /^[A-Z]{6}[A-Z0-9]{2,5}$/i;
    if (swiftPattern.test(trimmed)) {
      return { type: 'swift', value: trimmed, label: 'SWIFT' };
    }
    
    // Crypto wallet detection
    // Bitcoin (starts with 1, 3, or bc1)
    if (/^(1|3)[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed) || /^bc1[a-zA-HJ-NP-Z0-9]{39,59}$/.test(trimmed)) {
      return { type: 'crypto', value: trimmed, network: 'Bitcoin', label: 'BTC' };
    }
    // Ethereum/ERC-20/BEP-20 (starts with 0x, 40 hex chars)
    if (/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
      return { type: 'crypto', value: trimmed, network: 'ERC-20/BEP-20', label: 'ETH/BSC' };
    }
    // TRON/TRC-20 (starts with T, 34 chars)
    if (/^T[a-zA-HJ-NP-Z0-9]{33}$/.test(trimmed)) {
      return { type: 'crypto', value: trimmed, network: 'TRC-20', label: 'USDT TRC-20' };
    }
    // Generic crypto (long alphanumeric string without digits only)
    if (trimmed.length > 25 && /^[A-Za-z0-9]+$/.test(trimmed) && !/^\d+$/.test(trimmed)) {
      return { type: 'crypto', value: trimmed, network: 'Crypto', label: 'Crypto' };
    }
    
    // Card number detection (13-19 digits)
    const cardNumber = trimmed.match(/\d{13,19}/)?.[0];
    if (cardNumber) {
      const bin = cardNumber.substring(0, 6);
      let bank = '';
      
      // Ukrainian bank BINs
      if (bin.startsWith('5457') || bin.startsWith('5168') || bin.startsWith('4441')) bank = 'monobank';
      else if (bin.startsWith('5375') || bin.startsWith('4149')) bank = 'ПриватБанк';
      else if (bin.startsWith('5363')) bank = 'ПУМБ';
      else if (bin.startsWith('5355')) bank = 'Ощадбанк';
      else if (bin.startsWith('5167')) bank = 'Райффайзен';
      else if (bin.startsWith('4731')) bank = 'Укргазбанк';
      
      return { type: 'card', value: cardNumber, bank, label: 'Card' };
    }
    
    // Fallback - show as generic text
    return { type: 'other', value: trimmed, label: 'Other' };
  };
  
  // Get icon for payment type
  const getPaymentIcon = (type) => {
    switch (type) {
      case 'card': return <CreditCard className="h-3 w-3" />;
      case 'iban': return <Building2 className="h-3 w-3" />;
      case 'swift': return <Globe className="h-3 w-3" />;
      case 'crypto': return <Bitcoin className="h-3 w-3" />;
      default: return <Wallet className="h-3 w-3" />;
    }
  };

  // Task payments handlers
  const handleConfirmPayment = async (paymentId) => {
    try {
      await taskPaymentsApi.update(paymentId, "confirmed");
      toast.success("Payment confirmed");
      fetchData();
    } catch (error) {
      toast.error("Confirmation error");
    }
  };

  const handleMarkTaskPaid = async (paymentId) => {
    // Open paid details dialog
    setPaidDialog({ open: true, paymentId, type: "task" });
    setPaidDate(new Date().toISOString().split("T")[0]);
    setPaidSource("");
    setPaidComment("");
  };

  const handleConfirmTaskPaid = async () => {
    const { paymentId, type } = paidDialog;
    try {
      if (type === "task") {
        const params = new URLSearchParams({ status: "paid" });
        if (paidDate) params.append("paid_date", paidDate);
        if (paidSource) params.append("paid_source", paidSource);
        if (paidComment) params.append("paid_comment", paidComment);
        await api.put(`/task-payments/${paymentId}?${params.toString()}`);
      } else {
        await otherExpensesApi.changeStatus(paymentId, { 
          new_status: "paid",
          reason: "Paid from payments list",
          paid_date: paidDate || undefined,
          paid_source: paidSource || undefined,
          paid_comment: paidComment || undefined
        });
      }
      toast.success("Payment marked as paid");
      setPaidDialog({ open: false, paymentId: null, type: null });
      fetchData();
    } catch (error) {
      toast.error("Payment error");
    }
  };

  const handleDeleteTaskPayment = async (paymentId) => {
    try {
      await taskPaymentsApi.delete(paymentId);
      toast.success("Payment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Artist expense handlers - use changeStatus API for audit logging
  const handleApproveExpense = async (expenseId) => {
    try {
      await otherExpensesApi.changeStatus(expenseId, { 
        new_status: "approved",
        reason: "Quick approve from list"
      });
      toast.success("Approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  const handleCancelExpense = async (expenseId) => {
    try {
      await otherExpensesApi.changeStatus(expenseId, { 
        new_status: "cancelled",
        reason: "Quick cancel from list"
      });
      toast.success("Cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const handleMarkExpensePaid = async (expenseId) => {
    // Open paid details dialog for expenses
    setPaidDialog({ open: true, paymentId: expenseId, type: "expense" });
    setPaidDate(new Date().toISOString().split("T")[0]);
    setPaidSource("");
    setPaidComment("");
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await otherExpensesApi.delete(expenseId);
      toast.success("Deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleEditExpense = (expense) => {
    setEditAmount(expense.amount.toString());
    setEditDialog({ open: true, expense });
  };

  const handleViewExpenseDetails = (expenseId) => {
    navigate(`/payments/${expenseId}`);
  };

  const handleSaveEdit = async () => {
    if (!editDialog.expense) return;
    try {
      await otherExpensesApi.update(editDialog.expense.id, { 
        amount: parseFloat(editAmount) 
      });
      toast.success("Updated");
      setEditDialog({ open: false, expense: null });
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Get unique artists for filter
  const uniqueArtists = useMemo(() => {
    const artists = new Set();
    taskPayments.forEach(p => p.artist_name && artists.add(p.artist_name));
    artistExpenses.forEach(e => {
      const name = e.payee_name_en || e.payee_name;
      artists.add(name);
    });
    return Array.from(artists).sort();
  }, [taskPayments, artistExpenses]);

  // payment_due = approved_at (or client_confirmed_date) + 17d — kept in sync with
  // renderTaskPaymentsTable.getPaymentDueDate (Date object) for filter + export.
  const computeTaskDueIso = (p) => {
    const seed = p.approved_at || p.client_confirmed_date;
    if (!seed) return null;
    const d = new Date(seed);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + 17);
    return d.toISOString().slice(0, 10);
  };

  const inDueRange = (iso) => {
    if (!iso) return !filterDueFrom && !filterDueTo; // missing date passes only when no range set
    if (filterDueFrom && iso < filterDueFrom) return false;
    if (filterDueTo && iso > filterDueTo) return false;
    return true;
  };

  // Filter data
  const filteredTaskPayments = useMemo(() => {
    return taskPayments.filter(p => {
      if (filterStatus === "to_pay") {
        if (p.status !== "confirmed" && p.status !== "approved") return false;
      } else if (filterStatus !== "all" && p.status !== filterStatus) return false;
      if (filterArtist !== "all" && p.artist_name !== filterArtist) return false;
      if (filterMethod !== "all" && (p.payment_method || "other") !== filterMethod) return false;
      if (filterDueFrom || filterDueTo) {
        if (!inDueRange(computeTaskDueIso(p))) return false;
      }
      return true;
    });
  }, [taskPayments, filterStatus, filterArtist, filterMethod, filterDueFrom, filterDueTo]);

  const filteredArtistExpenses = useMemo(() => {
    return artistExpenses.filter(e => {
      // Hide cancelled by default unless specifically filtered
      if (filterStatus === "all" && e.status === "cancelled") return false;
      if (filterStatus === "to_pay") {
        if (e.status !== "confirmed" && e.status !== "approved") return false;
      } else if (filterStatus !== "all" && e.status !== filterStatus) return false;
      if (filterArtist !== "all") {
        const name = e.payee_name_en || e.payee_name;
        if (name !== filterArtist) return false;
      }
      if (filterMethod !== "all" && (e.payment_method || "other") !== filterMethod) return false;
      // External expenses don't have an approval cycle → filter against e.date directly.
      if (filterDueFrom || filterDueTo) {
        const iso = (e.date || "").slice(0, 10);
        if (!inDueRange(iso || null)) return false;
      }
      return true;
    });
  }, [artistExpenses, filterStatus, filterArtist, filterMethod, filterDueFrom, filterDueTo]);

  // Calculate stats with currency conversion to displayCurrency
  const taskStats = filteredTaskPayments.reduce(
    (acc, p) => {
      const convertedAmount = convertAmount(p.amount, p.currency || "USD");
      if (p.status === "paid") acc.paid += convertedAmount;
      else if (p.status === "confirmed" || p.status === "approved") acc.toPay += convertedAmount;
      else acc.pending += convertedAmount; // only "pending" status
      if (p.is_overdue) acc.overdue += 1;
      return acc;
    },
    { pending: 0, toPay: 0, paid: 0, overdue: 0 }
  );

  const expenseStats = filteredArtistExpenses.reduce(
    (acc, e) => {
      const convertedAmount = convertAmount(e.amount, e.currency || "UAH");
      if (e.status === "paid") acc.paid += convertedAmount;
      else acc.pending += convertedAmount;
      acc.total += convertedAmount;
      return acc;
    },
    { pending: 0, paid: 0, total: 0 }
  );

  const getDaysOverdue = (confirmedDate) => {
    if (!confirmedDate) return 0;
    const confirmed = new Date(confirmedDate);
    const now = new Date();
    const diffDays = Math.floor((now - confirmed) / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays - 14);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${monthNames[month - 1]} ${year}`;
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear++; }
    else if (newMonth < 1) { newMonth = 12; newYear--; }
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
  };

  const clearFilters = () => {
    setFilterStatus("all");
    setFilterArtist("all");
    setFilterMethod("all");
    setFilterDueFrom("");
    setFilterDueTo("");
  };

  const hasActiveFilters = filterStatus !== "all" || filterArtist !== "all" || filterMethod !== "all" || filterDueFrom || filterDueTo;

  const handleExport = (targetCurrency = "ORIGINAL") => {
    const convert = targetCurrency !== "ORIGINAL";
    const mapRow = (base) => {
      const native = Number(base.amount) || 0;
      const nativeCur = (base.currency || "UAH").toUpperCase();
      const exportedAmount = convert
        ? Math.round(convertAmountExplicit(native, nativeCur, targetCurrency) * 100) / 100
        : native;
      return {
        ...base,
        amount: exportedAmount,
        currency: convert ? targetCurrency : nativeCur,
        ...(convert ? { original_amount: native, original_currency: nativeCur } : {}),
      };
    };
    const taskRows = filteredTaskPayments.map((p) => mapRow({
      kind: "Task payment",
      date: p.confirmed_date || p.created_at?.slice(0, 10) || "",
      payment_due: computeTaskDueIso(p) || "",
      artist: p.artist_name || "",
      project: p.project_name || "",
      task: p.task_name || "",
      amount: p.amount ?? "",
      currency: p.currency || "",
      status: p.status || "",
      paid_date: p.paid_date || "",
      method: p.payment_method || "",
      notes: p.notes || "",
      month: selectedMonth,
    }));
    const expenseRows = filteredArtistExpenses.map((e) => mapRow({
      kind: "Artist expense",
      date: e.date || "",
      payment_due: "", // external expenses have no approval cycle / due date
      artist: e.payee_name_en || e.payee_name || "",
      project: e.project_name || "",
      task: "",
      amount: e.amount ?? "",
      currency: e.currency || "",
      status: e.status || "",
      paid_date: e.paid_date || "",
      method: e.payment_method || "",
      notes: e.notes || e.description || "",
      month: selectedMonth,
    }));
    const rows = [...taskRows, ...expenseRows];
    if (rows.length === 0) {
      toast.error("Nothing to export with current filters");
      return;
    }
    const columns = [
      { key: "kind", header: "Kind" },
      { key: "date", header: "Date" },
      { key: "payment_due", header: "Payment Due" },
      { key: "artist", header: "Artist" },
      { key: "project", header: "Project" },
      { key: "task", header: "Task" },
      { key: "amount", header: convert ? `Amount (${targetCurrency})` : "Amount" },
      { key: "currency", header: "Currency" },
      ...(convert ? [
        { key: "original_amount", header: "Original Amount" },
        { key: "original_currency", header: "Original Currency" },
      ] : []),
      { key: "status", header: "Status" },
      { key: "paid_date", header: "Paid Date" },
      { key: "method", header: "Method" },
      { key: "notes", header: "Notes" },
      { key: "month", header: "Month" },
    ];
    const preamble = buildExportPreamble(targetCurrency, rates, rateDate);
    exportToCsv(
      timestampedName(`artist-payments-${selectedMonth}${convert ? `-${targetCurrency}` : ""}`),
      rows,
      columns,
      preamble ? { preamble } : undefined,
    );
    toast.success(
      convert
        ? `Exported ${rows.length} rows · all amounts in ${targetCurrency}`
        : `Exported ${rows.length} rows`
    );
  };

  // Create new payment dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPayment, setNewPayment] = useState({
    payee_name: "",
    amount: "",
    currency: "UAH",
    expense_type: "artist_payment",
    project_name: "",
    payment_details: "",
    notes: "",
    date: new Date().toISOString().slice(0, 10),
    artist_id: "",
  });
  const [artists, setArtists] = useState([]);
  const [creating, setCreating] = useState(false);

  // Fetch artists for dropdown
  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const res = await fetch("/api/users?role=artist", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        const data = await res.json();
        setArtists(data || []);
      } catch (error) {
        console.error("Failed to fetch artists:", error);
      }
    };
    fetchArtists();
  }, []);

  const handleCreatePayment = async () => {
    if (!newPayment.payee_name || !newPayment.amount) {
      toast.error("Please fill payee name and amount");
      return;
    }
    setCreating(true);
    try {
      await otherExpensesApi.create({
        ...newPayment,
        amount: parseFloat(newPayment.amount),
        status: "pending"
      });
      toast.success("Payment created successfully");
      setCreateDialogOpen(false);
      setNewPayment({
        payee_name: "",
        amount: "",
        currency: "UAH",
        expense_type: "artist_payment",
        project_name: "",
        payment_details: "",
        notes: "",
        date: new Date().toISOString().slice(0, 10),
        artist_id: "",
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create payment");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Artist Payments</h1>
          <p className="text-sm text-zinc-500">
            {["admin", "finance"].includes(user?.role) ? "Manage payments to artists" : "Your payment status"}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportCurrencyButton onExport={handleExport} />
          {["admin", "finance"].includes(user?.role) && (
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Payment
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Task Pending</p>
            <p className="text-xl font-bold text-orange-400 font-mono">{getCurrencySymbol()}{taskStats.pending.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Task To Pay</p>
            <p className="text-xl font-bold text-sky-400 font-mono">{getCurrencySymbol()}{taskStats.toPay.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Task Paid</p>
            <p className="text-xl font-bold text-green-400 font-mono">{getCurrencySymbol()}{taskStats.paid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </CardContent>
        </Card>
        <Card 
          className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:bg-zinc-800/50 transition-colors"
          onClick={() => {
            // Show pending payments - navigate to filtered view
            setFilterStatus("approved");
            setSelectedMonth(""); // Clear month filter to see all pending
          }}
        >
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1 flex items-center gap-1">
              External Pending
              <span className="text-purple-400 text-[10px]">(click to view)</span>
            </p>
            <p className="text-xl font-bold text-purple-400 font-mono">
              {getCurrencySymbol()}{convertAmount((artistStats?.totals?.pending || 0) + (artistStats?.totals?.approved || 0), "UAH").toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
            {artistStats?.pending_expenses?.length > 0 && (
              <p className="text-[10px] text-zinc-500 mt-1">
                {artistStats.pending_expenses.length} pending payment(s)
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">External Paid</p>
            <p className="text-xl font-bold text-green-400 font-mono">{getCurrencySymbol()}{expenseStats.paid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </CardContent>
        </Card>
        <Card className={cn(
          "border-zinc-800",
          taskStats.overdue > 0 ? "bg-red-500/10 border-red-500/30" : "bg-zinc-900/50"
        )}>
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Overdue</p>
            <p className={cn("text-xl font-bold font-mono", taskStats.overdue > 0 ? "text-red-400" : "text-zinc-600")}>
              {taskStats.overdue}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            onClick={() => {
              if (!selectedMonth) {
                setSelectedMonth(new Date().toISOString().slice(0, 7));
              } else {
                navigateMonth(-1);
              }
            }} 
            className="text-zinc-400 hover:text-zinc-50"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <h2 className="text-lg font-semibold text-zinc-50 font-['Manrope'] min-w-[150px] text-center">
            {selectedMonth ? formatMonth(selectedMonth) : "All Pending Payments"}
          </h2>
          <Button 
            variant="ghost" 
            onClick={() => {
              if (!selectedMonth) {
                setSelectedMonth(new Date().toISOString().slice(0, 7));
              } else {
                navigateMonth(1);
              }
            }} 
            className="text-zinc-400 hover:text-zinc-50"
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          {!selectedMonth && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSelectedMonth(new Date().toISOString().slice(0, 7))}
              className="border-zinc-700 ml-2"
            >
              Back to Month View
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-zinc-500" />
          <Select value={filterArtist} onValueChange={setFilterArtist}>
            <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
              <SelectValue placeholder="Artist" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" className="text-zinc-50">All Artists</SelectItem>
              {uniqueArtists.map(artist => (
                <SelectItem key={artist} value={artist} className="text-zinc-50">{artist}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" className="text-zinc-50">All Status</SelectItem>
              <SelectItem value="pending" className="text-zinc-50">Pending</SelectItem>
              <SelectItem value="to_pay" className="text-zinc-50">To Pay</SelectItem>
              <SelectItem value="paid" className="text-zinc-50">Paid</SelectItem>
              <SelectItem value="cancelled" className="text-zinc-50">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterMethod} onValueChange={setFilterMethod}>
            <SelectTrigger className="w-[130px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9" data-testid="filter-payment-method">
              <SelectValue placeholder="Method" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" className="text-zinc-50">All Methods</SelectItem>
              <SelectItem value="card" className="text-zinc-50">Card</SelectItem>
              <SelectItem value="fop" className="text-zinc-50">FOP</SelectItem>
              <SelectItem value="crypto" className="text-zinc-50">Crypto</SelectItem>
              <SelectItem value="iban" className="text-zinc-50">IBAN</SelectItem>
              <SelectItem value="other" className="text-zinc-50">Other</SelectItem>
            </SelectContent>
          </Select>
          {/* marker: payments-due-date-filter-v1 */}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-zinc-900 border border-zinc-800 h-9">
            <span className="text-[10px] uppercase tracking-wide text-zinc-500">Due</span>
            <Input
              type="date"
              value={filterDueFrom}
              onChange={(e) => setFilterDueFrom(e.target.value)}
              className="bg-transparent border-0 text-xs text-zinc-50 h-6 w-[120px] p-0"
              title="Payment due from"
            />
            <span className="text-zinc-600">→</span>
            <Input
              type="date"
              value={filterDueTo}
              onChange={(e) => setFilterDueTo(e.target.value)}
              className="bg-transparent border-0 text-xs text-zinc-50 h-6 w-[120px] p-0"
              title="Payment due to"
            />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400 hover:text-zinc-50 h-9">
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Method Summary */}
      {(() => {
        const allPayments = [...filteredTaskPayments, ...filteredArtistExpenses];
        const summary = allPayments.reduce((acc, p) => {
          const method = p.payment_method || p.method || "other";
          if (!acc[method]) acc[method] = { pending: 0, paid: 0, count: 0 };
          const converted = convertAmount(p.amount || 0, p.currency || "USD");
          if (p.status === "paid") acc[method].paid += converted;
          else acc[method].pending += converted;
          acc[method].count++;
          return acc;
        }, {});
        const entries = Object.entries(summary).sort((a, b) => b[1].pending - a[1].pending);
        if (entries.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-2">
            {entries.map(([method, data]) => (
              <div key={method} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-800/70 border border-zinc-700/50 text-xs">
                <span className="text-zinc-400 capitalize">{method}:</span>
                {data.pending > 0 && <span className="text-yellow-400 font-mono">{getCurrencySymbol()}{data.pending.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                {data.pending > 0 && data.paid > 0 && <span className="text-zinc-600">/</span>}
                {data.paid > 0 && <span className="text-emerald-400 font-mono">{getCurrencySymbol()}{data.paid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                <span className="text-zinc-600">({data.count})</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="all" className="data-[state=active]:bg-zinc-800">
            All ({filteredTaskPayments.length + filteredArtistExpenses.length})
          </TabsTrigger>
          <TabsTrigger value="tasks" className="data-[state=active]:bg-zinc-800">
            <ListTodo className="h-4 w-4 mr-1" /> Task-Based ({filteredTaskPayments.length})
          </TabsTrigger>
          <TabsTrigger value="external" className="data-[state=active]:bg-zinc-800">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> External ({filteredArtistExpenses.length})
          </TabsTrigger>
        </TabsList>

        {/* All Payments */}
        <TabsContent value="all" className="space-y-4">
          {filteredTaskPayments.length > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
                  <ListTodo className="h-4 w-4 text-sky-400" /> Task-Based Payments
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {renderTaskPaymentsTable(filteredTaskPayments)}
              </CardContent>
            </Card>
          )}
          {filteredArtistExpenses.length > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-purple-400" /> External Payments ({formatMonth(selectedMonth)})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {renderArtistExpensesTable(filteredArtistExpenses)}
              </CardContent>
            </Card>
          )}
          {filteredTaskPayments.length === 0 && filteredArtistExpenses.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No payments found</p>
            </div>
          )}
        </TabsContent>

        {/* Task-Based Only */}
        <TabsContent value="tasks">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              {filteredTaskPayments.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <ListTodo className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No task-based payments</p>
                </div>
              ) : (
                renderTaskPaymentsTable(filteredTaskPayments)
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* External Only */}
        <TabsContent value="external">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              {filteredArtistExpenses.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <FileSpreadsheet className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No external payments for {formatMonth(selectedMonth)}</p>
                </div>
              ) : (
                renderArtistExpensesTable(filteredArtistExpenses)
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Monthly Summary */}
      {artistStats?.monthly_breakdown?.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800 mt-6">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-500" />
              Monthly History (All Artist Payments)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-medium">Month</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Total</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Paid</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Pending</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {artistStats.monthly_breakdown.slice(0, 12).map((row) => (
                  <TableRow 
                    key={row.month}
                    className={cn(
                      "border-zinc-800 hover:bg-zinc-800/50 cursor-pointer",
                      row.month === selectedMonth && "bg-zinc-800/30"
                    )}
                    onClick={() => setSelectedMonth(row.month)}
                  >
                    <TableCell>
                      <span className="text-zinc-50 font-medium text-sm">{formatMonth(row.month)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-zinc-50 font-medium">{getCurrencySymbol()}{convertAmount(row.total, "UAH").toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-green-400">{getCurrencySymbol()}{convertAmount(row.paid, "UAH").toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-orange-400">{getCurrencySymbol()}{convertAmount(row.pending, "UAH").toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {row.pending === 0 && row.paid > 0 ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
                          Complete
                        </Badge>
                      ) : row.pending > 0 ? (
                        <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                          Partial
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-500 border-zinc-700">
                          No Data
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* TOTAL Row */}
                {artistStats.totals && (
                  <TableRow className="border-zinc-800 bg-zinc-800/30 font-bold">
                    <TableCell>
                      <span className="text-zinc-50 font-bold">TOTAL</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-zinc-50 font-bold">{getCurrencySymbol()}{convertAmount(artistStats.totals.total, "UAH").toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-green-400 font-bold">{getCurrencySymbol()}{convertAmount(artistStats.totals.paid, "UAH").toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-orange-400 font-bold">{getCurrencySymbol()}{convertAmount(artistStats.totals.pending + (artistStats.totals.approved || 0), "UAH").toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, expense: editDialog.expense })}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Edit Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <p className="text-zinc-400 text-sm mb-2">
                Artist: <span className="text-zinc-50">{editDialog.expense?.payee_name_en || editDialog.expense?.payee_name}</span>
              </p>
              <p className="text-zinc-400 text-sm">
                Project: <span className="text-zinc-50">{editDialog.expense?.project_name || "—"}</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Amount (UAH)</Label>
              <Input
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-zinc-50"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, expense: null })} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} className="bg-orange-500 hover:bg-orange-600">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Payment Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Plus className="h-5 w-5 text-orange-400" />
              Add New Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Artist Selection - FIRST, so name auto-fills */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-1">
                <User className="h-3 w-3" /> Select Artist *
              </Label>
              <Select
                value={newPayment.artist_id || "none"}
                onValueChange={(v) => {
                  const selectedArtist = artists.find(a => a.id === v);
                  setNewPayment({ 
                    ...newPayment, 
                    artist_id: v === "none" ? "" : v,
                    // Auto-fill payee name from selected artist
                    payee_name: selectedArtist ? selectedArtist.name : newPayment.payee_name
                  });
                }}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                  <SelectValue placeholder="Select artist..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="none" className="text-zinc-400">Other (manual entry)</SelectItem>
                  {artists.map((artist) => (
                    <SelectItem key={artist.id} value={artist.id} className="text-zinc-50">
                      {artist.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Payee Name - only show if no artist selected or for manual override */}
            {!newPayment.artist_id && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Payee Name *</Label>
                <Input
                  value={newPayment.payee_name}
                  onChange={(e) => setNewPayment({ ...newPayment, payee_name: e.target.value })}
                  placeholder="Full name (for non-artist payments)"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                />
              </div>
            )}

            {/* Show selected artist name */}
            {newPayment.artist_id && (
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <p className="text-xs text-purple-400 uppercase">Payment for Artist</p>
                <p className="text-zinc-50 font-medium">{newPayment.payee_name}</p>
              </div>
            )}

            {/* Amount & Currency */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Amount *</Label>
                <Input
                  type="number"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  placeholder="0"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Currency</Label>
                <Select
                  value={newPayment.currency}
                  onValueChange={(v) => setNewPayment({ ...newPayment, currency: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                    <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                    <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Date</Label>
              <Input
                type="date"
                value={newPayment.date}
                onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-50"
              />
            </div>

            {/* Project */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Project Name</Label>
              <Input
                value={newPayment.project_name}
                onChange={(e) => setNewPayment({ ...newPayment, project_name: e.target.value })}
                placeholder="Project name"
                className="bg-zinc-950 border-zinc-800 text-zinc-50"
              />
            </div>

            {/* Payment Details */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-1">
                <CreditCard className="h-3 w-3" /> Payment Details (Card/IBAN)
              </Label>
              <Input
                value={newPayment.payment_details}
                onChange={(e) => setNewPayment({ ...newPayment, payment_details: e.target.value })}
                placeholder="Card number, IBAN, or crypto wallet"
                className="bg-zinc-950 border-zinc-800 text-zinc-50 font-mono"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Notes</Label>
              <Textarea
                value={newPayment.notes}
                onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                placeholder="Description or notes..."
                className="bg-zinc-950 border-zinc-800 text-zinc-50 min-h-[60px]"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleCreatePayment}
              disabled={creating}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Details Modal */}
      <Dialog open={paymentDetailsModal.open} onOpenChange={(open) => !open && setPaymentDetailsModal({ open: false, payment: null, paymentMethods: null, loading: false })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Banknote className="h-5 w-5 text-purple-400" />
              Реквізити для оплати
            </DialogTitle>
          </DialogHeader>
          
          {paymentDetailsModal.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              {/* Payment Info */}
              {paymentDetailsModal.payment && (
                <div className="p-4 bg-zinc-800/50 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Отримувач:</span>
                    <span className="text-zinc-50 font-medium">{paymentDetailsModal.payment.artist_name || paymentDetailsModal.employeeName || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Сума:</span>
                    <span className="text-orange-400 font-mono font-bold">{formatAmount(paymentDetailsModal.payment.amount, paymentDetailsModal.payment.currency || "USD")}</span>
                  </div>
                  {paymentDetailsModal.payment.task_name && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Задача:</span>
                      <span className="text-zinc-50 text-sm">{paymentDetailsModal.payment.task_name}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Methods */}
              {paymentDetailsModal.paymentMethods && (
                paymentDetailsModal.paymentMethods.card?.card_number ||
                paymentDetailsModal.paymentMethods.card?.recipient_iban ||
                paymentDetailsModal.paymentMethods.fop?.iban ||
                paymentDetailsModal.paymentMethods.crypto?.wallet_address
              ) ? (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-zinc-400">Реквізити:</h4>
                  
                  {/* Preferred method badge */}
                  {paymentDetailsModal.paymentMethods.preferred_method && (
                    <Badge className="bg-green-900/30 text-green-400 border-green-700 mb-2">
                      Preferred: {paymentDetailsModal.paymentMethods.preferred_method === "card" ? "Карта" : paymentDetailsModal.paymentMethods.preferred_method === "fop" ? "ФОП" : "Крипто"}
                    </Badge>
                  )}

                  {/* Card */}
                  {paymentDetailsModal.paymentMethods.card && (paymentDetailsModal.paymentMethods.card.card_number || paymentDetailsModal.paymentMethods.card.recipient_iban) && (
                    <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="h-4 w-4 text-green-400" />
                        <span className="text-green-400 font-medium">Карта</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        {paymentDetailsModal.paymentMethods.card.card_number && (
                          <div className="flex justify-between items-center">
                            <span className="text-zinc-400">Номер карти:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-50 font-mono">{paymentDetailsModal.paymentMethods.card.card_number}</span>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentDetailsModal.paymentMethods.card.card_number); toast.success("Скопійовано"); }}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                        {paymentDetailsModal.paymentMethods.card.bank && (
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Банк:</span>
                            <span className="text-zinc-50">{paymentDetailsModal.paymentMethods.card.bank}</span>
                          </div>
                        )}
                        {paymentDetailsModal.paymentMethods.card.recipient_name && (
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Отримувач:</span>
                            <span className="text-zinc-50">{paymentDetailsModal.paymentMethods.card.recipient_name}</span>
                          </div>
                        )}
                        {paymentDetailsModal.paymentMethods.card.recipient_iban && (
                          <div className="flex justify-between items-center">
                            <span className="text-zinc-400">IBAN:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-50 font-mono text-xs">{paymentDetailsModal.paymentMethods.card.recipient_iban}</span>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentDetailsModal.paymentMethods.card.recipient_iban); toast.success("Скопійовано"); }}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                        {paymentDetailsModal.paymentMethods.card.recipient_ipn && (
                          <div className="flex justify-between">
                            <span className="text-zinc-400">ІПН:</span>
                            <span className="text-zinc-50 font-mono">{paymentDetailsModal.paymentMethods.card.recipient_ipn}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* FOP */}
                  {paymentDetailsModal.paymentMethods.fop && (paymentDetailsModal.paymentMethods.fop.iban || paymentDetailsModal.paymentMethods.fop.company_name) && (
                    <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 text-purple-400" />
                        <span className="text-purple-400 font-medium">ФОП</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        {paymentDetailsModal.paymentMethods.fop.company_name && (
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Назва:</span>
                            <span className="text-zinc-50">{paymentDetailsModal.paymentMethods.fop.company_name}</span>
                          </div>
                        )}
                        {paymentDetailsModal.paymentMethods.fop.iban && (
                          <div className="flex justify-between items-center">
                            <span className="text-zinc-400">IBAN:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-50 font-mono text-xs">{paymentDetailsModal.paymentMethods.fop.iban}</span>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentDetailsModal.paymentMethods.fop.iban); toast.success("Скопійовано"); }}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                        {paymentDetailsModal.paymentMethods.fop.edrpou && (
                          <div className="flex justify-between">
                            <span className="text-zinc-400">ЄДРПОУ:</span>
                            <span className="text-zinc-50 font-mono">{paymentDetailsModal.paymentMethods.fop.edrpou}</span>
                          </div>
                        )}
                        {paymentDetailsModal.paymentMethods.fop.bank_name && (
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Банк:</span>
                            <span className="text-zinc-50">{paymentDetailsModal.paymentMethods.fop.bank_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Crypto */}
                  {paymentDetailsModal.paymentMethods.crypto && paymentDetailsModal.paymentMethods.crypto.wallet_address && (
                    <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex items-center gap-2 mb-2">
                        <Bitcoin className="h-4 w-4 text-orange-400" />
                        <span className="text-orange-400 font-medium">Крипто</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        {paymentDetailsModal.paymentMethods.crypto.network && (
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Мережа:</span>
                            <span className="text-zinc-50">{paymentDetailsModal.paymentMethods.crypto.network}</span>
                          </div>
                        )}
                        {paymentDetailsModal.paymentMethods.crypto.wallet_address && (
                          <div className="flex justify-between items-center">
                            <span className="text-zinc-400">Гаманець:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-50 font-mono text-xs truncate max-w-[200px]">{paymentDetailsModal.paymentMethods.crypto.wallet_address}</span>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentDetailsModal.paymentMethods.crypto.wallet_address); toast.success("Скопійовано"); }}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <Wallet className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>Реквізити не заповнені</p>
                  <p className="text-xs mt-1">Артист ще не вказав свої платіжні дані</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Paid Details Dialog */}
      <Dialog open={paidDialog.open} onOpenChange={(v) => !v && setPaidDialog({ open: false, paymentId: null, type: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Детали оплаты</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Дата оплаты</label>
              <Input
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 h-9"
                data-testid="paid-date-input"
              />
            </div>
            <PaymentSourceSelector value={paidSource} onChange={setPaidSource} />
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Комментарий</label>
              <Textarea
                value={paidComment}
                onChange={(e) => setPaidComment(e.target.value)}
                placeholder="Примечание к платежу..."
                className="bg-zinc-800 border-zinc-700 text-zinc-50 h-16 resize-none text-sm"
                data-testid="paid-comment-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" className="border-zinc-700" onClick={() => setPaidDialog({ open: false, paymentId: null, type: null })}>
                Cancel
              </Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleConfirmTaskPaid} data-testid="confirm-paid-btn">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Mark Paid
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderTaskPaymentsTable(payments) {
    // Calculate payment due date (approved_at + 17 days) and sort by it
    const getPaymentDueDate = (payment) => {
      const approvedDate = payment.approved_at || payment.client_confirmed_date;
      if (!approvedDate) return null;
      const due = new Date(approvedDate);
      due.setDate(due.getDate() + 17);
      return due;
    };

    const isPaymentOverdue = (payment) => {
      if (payment.status === "paid") return false;
      const dueDate = getPaymentDueDate(payment);
      if (!dueDate) return false;
      return new Date() > dueDate;
    };

    const getDaysUntilDue = (payment) => {
      const dueDate = getPaymentDueDate(payment);
      if (!dueDate) return null;
      const now = new Date();
      const diffTime = dueDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    // Sort by payment due date (earliest first), paid payments at the end
    const sortedPayments = [...payments].sort((a, b) => {
      // Paid payments go to the end
      if (a.status === "paid" && b.status !== "paid") return 1;
      if (a.status !== "paid" && b.status === "paid") return -1;
      
      const dueDateA = getPaymentDueDate(a);
      const dueDateB = getPaymentDueDate(b);
      
      if (!dueDateA && !dueDateB) return 0;
      if (!dueDateA) return 1;
      if (!dueDateB) return -1;
      
      return dueDateA - dueDateB;
    });

    return (
      <Table>
        <TableHeader>
          <TableRow className="border-zinc-800 hover:bg-transparent">
            <TableHead className="text-zinc-500 font-medium text-xs uppercase">Task</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase">Artist</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase text-center">Method</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase text-right">Amount</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase text-center">Approved</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase text-center">Payment Due</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase text-center">Paid Date</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase text-center">Status</TableHead>
            <TableHead className="w-36"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedPayments.map((payment) => {
            const dueDate = getPaymentDueDate(payment);
            const daysUntilDue = getDaysUntilDue(payment);
            const isOverdue = isPaymentOverdue(payment);
            
            return (
              <TableRow 
                key={payment.id} 
                className={cn(
                  "border-zinc-800 hover:bg-zinc-800/50", 
                  isOverdue && "bg-red-500/10 border-l-2 border-l-red-500"
                )}
              >
                <TableCell>
                  <div>
                    <Link to={`/tasks/${payment.task_id}`} className="text-zinc-50 font-medium text-sm hover:text-orange-400 flex items-center gap-1">
                      {payment.task_name}
                      <ExternalLink className="h-3 w-3 opacity-50" />
                    </Link>
                    <p className="text-xs text-zinc-500">{payment.project_name}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-300">{payment.artist_name || "—"}</span>
                    {payment.payment_type === "art_director_fee" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-medium">AD Fee</span>
                    )}
                    {payment.artist_name && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openPaymentDetailsModal(payment, "task"); }}
                        className="h-6 px-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                        title="Реквізити для оплати"
                      >
                        <Banknote className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <span className={cn("text-xs px-1.5 py-0.5 rounded capitalize",
                    payment.payment_method === "fop" ? "bg-blue-500/15 text-blue-400" :
                    payment.payment_method === "card" ? "bg-green-500/15 text-green-400" :
                    payment.payment_method === "crypto" ? "bg-purple-500/15 text-purple-400" :
                    "bg-zinc-800 text-zinc-500"
                  )}>
                    {payment.payment_method || "—"}
                  </span>
                </TableCell>
                <TableCell className="text-right"><span className="font-mono text-zinc-50">{formatAmount(payment.amount, payment.currency || "USD")}</span></TableCell>
                <TableCell className="text-center">
                  {payment.approved_at || payment.client_confirmed_date ? (
                    <span className="text-xs text-zinc-400 font-mono">
                      {new Date(payment.approved_at || payment.client_confirmed_date).toLocaleDateString()}
                    </span>
                  ) : <span className="text-xs text-zinc-600">—</span>}
                </TableCell>
                <TableCell className="text-center">
                  {payment.status === "paid" ? (
                    <span className="text-xs text-green-400">Paid</span>
                  ) : dueDate ? (
                    <div className="flex flex-col items-center">
                      <span className={cn(
                        "text-xs font-mono",
                        isOverdue ? "text-red-400 font-bold" : daysUntilDue <= 3 ? "text-orange-400" : "text-zinc-300"
                      )}>
                        {dueDate.toLocaleDateString()}
                      </span>
                      {isOverdue ? (
                        <span className="text-[10px] text-red-400 flex items-center gap-0.5 font-medium">
                          <AlertTriangle className="h-3 w-3" />
                          {Math.abs(daysUntilDue)}d overdue
                        </span>
                      ) : daysUntilDue <= 3 ? (
                        <span className="text-[10px] text-orange-400">{daysUntilDue}d left</span>
                      ) : null}
                    </div>
                  ) : <span className="text-xs text-zinc-600">—</span>}
                </TableCell>
                <TableCell className="text-center">
                  {payment.paid_date ? (
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-xs text-green-400 font-mono">
                        {new Date(payment.paid_date).toLocaleDateString()}
                      </span>
                      {payment.paid_by_name && (
                        <span className="text-[10px] text-zinc-500">{payment.paid_by_name}</span>
                      )}
                      {payment.paid_source && (
                        <span className="text-[10px] text-blue-400/70">{payment.paid_source}</span>
                      )}
                    </div>
                  ) : <span className="text-xs text-zinc-600">—</span>}
                </TableCell>
                <TableCell className="text-center">
                  {payment.status === "paid" ? (
                    <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>
                  ) : payment.status === "confirmed" || payment.status === "approved" ? (
                    <Badge variant="outline" className={cn(isOverdue ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-sky-500/10 text-sky-400 border-sky-500/20")}>
                      {isOverdue && <AlertTriangle className="h-3 w-3 mr-1" />}To Pay
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20"><Clock className="h-3 w-3 mr-1" />Pending</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {["admin", "finance"].includes(user?.role) && (
                    <div className="flex gap-1 justify-end">
                      {/* View/Edit button */}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => navigate(`/task-payments/${payment.id}`)}
                        className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 h-7 px-2"
                        title="View Details"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      
                      {/* Confirm button for pending status */}
                      {payment.status === "pending" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleConfirmPayment(payment.id)} 
                          className="text-sky-400 border-sky-500/30 hover:bg-sky-500/10 h-7 px-2 text-xs"
                        >
                          <ThumbsUp className="h-3 w-3 mr-1" />Approve
                        </Button>
                      )}
                      
                      {/* Pay button for confirmed/approved status */}
                      {(payment.status === "confirmed" || payment.status === "approved") && (
                        <Button 
                          size="sm" 
                          onClick={() => handleMarkTaskPaid(payment.id)} 
                          className={cn("h-7 px-2 text-xs", isOverdue ? "bg-red-600 hover:bg-red-700 text-white" : "bg-green-600 hover:bg-green-700 text-white")}
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />Pay
                        </Button>
                      )}
                      
                      {/* Delete button */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"><Trash2 className="h-3 w-3" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-zinc-50">Delete Payment</AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-400">Are you sure? This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteTaskPayment(payment.id)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  function renderArtistExpensesTable(expenses) {
    return (
      <Table>
        <TableHeader>
          <TableRow className="border-zinc-800 hover:bg-transparent">
            <TableHead className="text-zinc-500 font-medium text-xs uppercase">Date</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase">Artist</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase">Payment Details</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase">Project</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase text-right">Amount</TableHead>
            <TableHead className="text-zinc-500 font-medium text-xs uppercase text-center">Status</TableHead>
            <TableHead className="w-44"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {expenses.map((expense) => {
            const paymentInfo = parsePaymentDetails(expense.payment_details);
            
            return (
            <TableRow key={expense.id} className={cn("border-zinc-800 hover:bg-zinc-800/50", expense.status === "cancelled" && "opacity-50")}>
              <TableCell>
                <span className="text-sm text-zinc-300 font-mono">{expense.date ? new Date(expense.date).toLocaleDateString() : "—"}</span>
              </TableCell>
              <TableCell>
                <div>
                  <Button 
                    variant="link" 
                    onClick={() => handleViewExpenseDetails(expense.id)}
                    className="text-zinc-50 font-medium text-sm p-0 h-auto hover:text-orange-400"
                  >
                    {expense.payee_name_en || expense.payee_name}
                  </Button>
                  {expense.payee_name_en && expense.payee_name_en !== expense.payee_name && (
                    <p className="text-xs text-zinc-500">{expense.payee_name}</p>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {paymentInfo || expense.artist_card_number || expense.artist_iban ? (
                  <div className="text-xs space-y-0.5">
                    {expense.artist_recipient_name && (
                      <p className="text-zinc-300">{expense.artist_recipient_name}</p>
                    )}
                    {/* Card */}
                    {paymentInfo?.type === 'card' && (
                      <>
                        <p className="text-zinc-400 font-mono flex items-center gap-1">
                          <CreditCard className="h-3 w-3 text-zinc-500" /> {paymentInfo.value}
                        </p>
                        {paymentInfo.bank && <p className="text-zinc-500 text-[10px]">{paymentInfo.bank}</p>}
                      </>
                    )}
                    {/* IBAN from parsing */}
                    {paymentInfo?.type === 'iban' && (
                      <p className="text-zinc-400 font-mono text-[10px] flex items-center gap-1">
                        <Building2 className="h-3 w-3 text-zinc-500" /> {paymentInfo.value}
                      </p>
                    )}
                    {/* SWIFT */}
                    {paymentInfo?.type === 'swift' && (
                      <p className="text-zinc-400 font-mono flex items-center gap-1">
                        <Globe className="h-3 w-3 text-zinc-500" /> {paymentInfo.value}
                      </p>
                    )}
                    {/* Crypto */}
                    {paymentInfo?.type === 'crypto' && (
                      <p className="text-zinc-400 font-mono text-[10px] flex items-center gap-1">
                        <Bitcoin className="h-3 w-3 text-amber-500" /> 
                        <span>{paymentInfo.network}</span>
                        <span className="text-zinc-600">({paymentInfo.value.substring(0, 8)}...)</span>
                      </p>
                    )}
                    {/* Other/fallback from parsing */}
                    {paymentInfo?.type === 'other' && (
                      <p className="text-zinc-400 text-[10px] flex items-center gap-1">
                        <Wallet className="h-3 w-3 text-zinc-500" /> {paymentInfo.value}
                      </p>
                    )}
                    {/* Fallback to artist_card_number from employee */}
                    {!paymentInfo && expense.artist_card_number && (
                      <p className="text-zinc-400 font-mono flex items-center gap-1">
                        <CreditCard className="h-3 w-3 text-zinc-500" /> {expense.artist_card_number}
                      </p>
                    )}
                    {/* IBAN from employee */}
                    {expense.artist_iban && !paymentInfo && (
                      <p className="text-zinc-400 font-mono text-[10px] flex items-center gap-1">
                        <Building2 className="h-3 w-3 text-zinc-500" /> {expense.artist_iban}
                      </p>
                    )}
                    {expense.artist_ipn && (
                      <p className="text-zinc-500">ІПН: {expense.artist_ipn}</p>
                    )}
                    {expense.artist_bank && !paymentInfo?.bank && (
                      <p className="text-zinc-500 text-[10px]">{expense.artist_bank}</p>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-zinc-600 italic">Not set</span>
                )}
              </TableCell>
              <TableCell><span className="text-sm text-zinc-400">{expense.project_name || "—"}</span></TableCell>
              <TableCell className="text-right"><span className="font-mono text-zinc-50">{formatAmount(expense.amount, expense.currency)}</span></TableCell>
              <TableCell className="text-center">
                {expense.status === "paid" ? (
                  <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>
                ) : expense.status === "approved" ? (
                  <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20"><ThumbsUp className="h-3 w-3 mr-1" />Approved</Badge>
                ) : expense.status === "cancelled" ? (
                  <Badge variant="outline" className="bg-zinc-700/50 text-zinc-500 border-zinc-600"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>
                ) : (
                  <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20"><Clock className="h-3 w-3 mr-1" />Pending</Badge>
                )}
              </TableCell>
              <TableCell>
                {["admin", "finance"].includes(user?.role) && (
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => handleViewExpenseDetails(expense.id)} className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 h-7 px-2" title="View Details">
                      <Eye className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEditExpense(expense)} className="text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800 h-7 px-2" title="Edit Amount">
                      <Edit className="h-3 w-3" />
                    </Button>
                    {expense.status === "pending" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => handleApproveExpense(expense.id)} className="text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 h-7 px-2" title="Approve">
                          <ThumbsUp className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleCancelExpense(expense.id)} className="text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700 h-7 px-2" title="Cancel">
                          <XCircle className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                    {expense.status === "approved" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => handleMarkExpensePaid(expense.id)} className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-7 px-2" title="Mark Paid">
                          <CheckCircle2 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleCancelExpense(expense.id)} className="text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700 h-7 px-2" title="Cancel">
                          <XCircle className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"><Trash2 className="h-3 w-3" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-zinc-50">Delete Payment</AlertDialogTitle>
                          <AlertDialogDescription className="text-zinc-400">Are you sure? This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteExpense(expense.id)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </TableCell>
            </TableRow>
          );
          })}
        </TableBody>
      </Table>
    );
  }
};

export default PaymentsPage;
